import { format } from 'date-fns';
import { SESClient, SendEmailCommand } from "@aws-sdk/client-ses";

console.log('🚀 Loading UPDATED unifiedEmailService.js - ' + new Date().toISOString());

// Configure AWS SES
const region = import.meta.env.VITE_AWS_REGION;
const fromEmail = import.meta.env.VITE_AWS_SES_FROM_EMAIL;
const configurationSet = import.meta.env.VITE_AWS_SES_CONFIG_SET;  // Match the env variable name

// Log configuration
console.log('AWS SES Configuration:', {
  region,
  fromEmail,
  configurationSet,
  env: {
    VITE_AWS_REGION: import.meta.env.VITE_AWS_REGION,
    VITE_AWS_SES_FROM_EMAIL: import.meta.env.VITE_AWS_SES_FROM_EMAIL,
    VITE_AWS_SES_CONFIG_SET: import.meta.env.VITE_AWS_SES_CONFIG_SET
  }
});

const sesClient = new SESClient({
  region,
  credentials: {
    accessKeyId: import.meta.env.VITE_AWS_ACCESS_KEY_ID,
    secretAccessKey: import.meta.env.VITE_AWS_SECRET_ACCESS_KEY,
  },
});

console.log('AWS Configuration:', {
  region,
  fromEmail,
  accessKeyId: import.meta.env.VITE_AWS_ACCESS_KEY_ID?.substring(0, 5) + '...',
  endpoint: `https://email.${region}.amazonaws.com`
});

// Private utility functions
const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  const digits = phoneNumber.replace(/\D/g, '').replace(/^1/, '');
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  return phoneNumber;
};

const wrapHtmlContent = (content) => `
  <!DOCTYPE html>
  <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <style>
        body {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          color: #2d3748;
          margin: 0;
          padding: 24px;
        }
        .email-content {
          max-width: 600px;
          margin: 0 auto;
        }
      </style>
    </head>
    <body>
      <div class="email-content">
        ${content}
      </div>
    </body>
  </html>
`;

// The main service
const unifiedEmailService = {
  // Expose replaceVariables as a service method
  replaceVariables(content, variables) {
    if (!content) return '';
    
    let processedContent = content;
    console.log('Starting variable replacement with:', { 
      contentPreview: content.substring(0, 100),
      variables 
    });

    // Format phone number if available (expecting E.164 format input)
    let phoneFormats = {};
    if (variables.phone) {
      console.log('Processing phone number:', {
        input: variables.phone,
        isE164: /^\+1\d{10}$/.test(variables.phone)
      });

      // Remove +1 prefix and clean any non-digits
      const cleaned = variables.phone.replace(/^\+1/, '').replace(/\D/g, '');
      console.log('Cleaned phone number:', {
        cleaned,
        length: cleaned.length
      });

      if (cleaned.length === 10) {
        const areaCode = cleaned.slice(0, 3);
        const prefix = cleaned.slice(3, 6);
        const line = cleaned.slice(6, 10);
        
        phoneFormats = {
          'phone:unformattedLong': `1${cleaned}`,
          'phone:unformattedShort': cleaned,
          'phone:formattedLong': `+1 (${areaCode}) ${prefix}-${line}`,
          'phone:formattedShort': `(${areaCode}) ${prefix}-${line}`,
          'phone': variables.phone  // Keep original E.164 format
        };

        console.log('Phone formats:', phoneFormats);
      } else {
        console.warn('Invalid phone number length:', cleaned.length);
      }
    }

    // Handle date variables with format
    const now = new Date();
    const dateFormats = {
      'sendDate:full': format(now, 'EEEE, MMMM dd, yyyy'),
      'sendDate:long': format(now, 'MMMM dd, yyyy'),
      'sendDate:medium': format(now, 'MMM dd, yyyy'),
      'sendDate:short': format(now, 'M/d/yyyy'),
      'sendDate': format(now, 'MMMM dd, yyyy')  // default to long format
    };

    // Process basic variables (excluding phone and date which are handled above)
    const basicVariables = Object.entries(variables).reduce((acc, [key, value]) => {
      if (key !== 'phone') {  // Skip phone since we handle it separately
        acc[key] = value;
      }
      return acc;
    }, {});

    // Combine all variables and wrap with {{}}
    const allVariables = Object.entries({
      ...basicVariables,
      ...phoneFormats,
      ...dateFormats
    }).reduce((acc, [key, value]) => {
      acc[`{{${key}}}`] = value;
      return acc;
    }, {});

    console.log('All variables for substitution:', allVariables);

    // Handle all variables
    Object.entries(allVariables).forEach(([key, value]) => {
      const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      const matches = processedContent.match(regex);
      console.log('Variable replacement:', { 
        key, 
        value, 
        matches: matches?.length || 0,
        regex: regex.toString()
      });
      processedContent = processedContent.replace(regex, value || `[${key}]`);
    });

    console.log('Final content preview:', processedContent.substring(0, 100));
    return processedContent;
  },

  // Send an email using a named template
  async sendTemplateEmail(templateName, to, variables = {}, options = {}) {
    let fetchedTemplate = null;
    
    try {
      // Fetch template
      const { data: template, error: templateError } = await supabase
        .from('email_templates')
        .select('*')
        .eq('name', templateName)
        .single();

      if (templateError) throw templateError;
      if (!template) throw new Error(`Template ${templateName} not found`);
      
      console.log('Found template:', template);
      console.log('Input variables:', variables);
      
      fetchedTemplate = template;

      // Process variables with all date formats and defaults
      const now = new Date();
      const processedVariables = {
        ...variables,
        sendDate: format(now, 'MMMM dd, yyyy'),
        'sendDate:long': format(now, 'MMMM dd, yyyy'),
        'sendDate:medium': format(now, 'MMM dd, yyyy'),
        'sendDate:short': format(now, 'M/d/yyyy'),
        'sendDate:full': format(now, 'EEEE, MMMM dd, yyyy'),
        clubName: options.clubName || import.meta.env.VITE_CLUB_NAME || import.meta.env.CLUB_NAME || 'Eden Prairie Noon Rotary'
      };

      console.log('Processed variables:', processedVariables);

      // Replace variables in content
      const html = this.replaceVariables(template.html_content, processedVariables);
      const text = this.replaceVariables(template.text_content, processedVariables);
      const subject = this.replaceVariables(template.subject, processedVariables);

      console.log('Processed content:', {
        subject,
        html: html.substring(0, 1000) + '...',
        text: text.substring(0, 1000) + '...',
        template_html: template.html_content.substring(0, 1000) + '...',
        template_text: template.text_content.substring(0, 1000) + '...'
      });

      // Prepare email parameters
      const emailParams = {
        to,
        subject,
        html,
        text,
        sender: fromEmail,
        ...options
      };

      // Send email
      const result = await this.sendEmail(emailParams);

      // Log the email with all processed variables
      await this.logEmail({
        template_id: template.id,
        message_id: result.messageId,
        recipient_email: to,
        recipient_name: variables.fullName || `${variables.firstName} ${variables.lastName}`.trim(),
        sender: emailParams.sender,
        subject,
        content_html: html,
        content_text: text,
        variables: processedVariables,
        status: 'sent',
        aws_response: result,
        configuration_set: configurationSet,
        ...options
      });

      return result;
    } catch (error) {
      // Log failed email attempts
      await this.logEmail({
        template_id: fetchedTemplate?.id,
        recipient_email: to,
        recipient_name: variables.fullName || `${variables.firstName} ${variables.lastName}`.trim(),
        sender: fromEmail,
        subject: fetchedTemplate?.subject,
        status: 'failed',
        error_message: error.message
      });

      console.error('Failed to send template email:', error);
      throw error;
    }
  },

  // Send a direct email
  async sendEmail({
    to,
    subject,
    html,
    text,
    variables = {},
    sender,
    template_id,
    supabase,
    ...options
  }) {
    console.log('sendEmail called with:', {
      to,
      subject,
      variables,
      contentPreview: html?.substring(0, 100),
      hasSupabase: !!supabase
    });

    // Process the content through replaceVariables first
    const processedHtml = this.replaceVariables(html, variables);
    const processedText = this.replaceVariables(text, variables);
    const processedSubject = this.replaceVariables(subject, variables);

    console.log('After variable replacement:', {
      subject: processedSubject,
      contentPreview: processedHtml?.substring(0, 100)
    });

    const params = {
      Destination: {
        ToAddresses: [to]
      },
      Message: {
        Body: {
          Html: {
            Data: wrapHtmlContent(processedHtml)
          },
          Text: {
            Data: processedText
          }
        },
        Subject: {
          Data: processedSubject
        }
      },
      Source: sender || fromEmail,
      ConfigurationSetName: configurationSet
    };

    try {
      const command = new SendEmailCommand(params);
      const result = await sesClient.send(command);
      
      // Process variables for logging
      const now = new Date();
      const processedVariables = {
        ...variables,  // Start with original variables except phone
        sendDate: format(now, 'MMMM dd, yyyy'),
        'sendDate:full': format(now, 'EEEE, MMMM dd, yyyy'),
        'sendDate:long': format(now, 'MMMM dd, yyyy'),
        'sendDate:medium': format(now, 'MMM dd, yyyy'),
        'sendDate:short': format(now, 'M/d/yyyy')
      };

      // Add phone formats if phone is present
      if (variables.phone) {
        const cleaned = variables.phone.replace(/^\+1/, '').replace(/\D/g, '');
        if (cleaned.length === 10) {
          const areaCode = cleaned.slice(0, 3);
          const prefix = cleaned.slice(3, 6);
          const line = cleaned.slice(6, 10);
          
          // Only add formatted versions, not the original
          processedVariables['phone:unformattedLong'] = `1${cleaned}`;
          processedVariables['phone:unformattedShort'] = cleaned;
          processedVariables['phone:formattedLong'] = `+1 (${areaCode}) ${prefix}-${line}`;
          processedVariables['phone:formattedShort'] = `(${areaCode}) ${prefix}-${line}`;
        }
      }

      // Log the email if we have a Supabase client
      if (supabase) {
        try {
          // Look up the recipient_id by email address
          let recipient_id = null;
          if (to) {
            const { data: contact } = await supabase
              .from('texting_contacts')
              .select('id')
              .eq('email', to)
              .single();
            
            recipient_id = contact?.id;
          }

          // Get the current user's information
          const { data: { user } } = await supabase.auth.getUser();
          let initiator_name = null;

          if (user) {
            // Get the user's profile information
            const { data: profile } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', user.id)
              .single();

            if (profile) {
              initiator_name = `${profile.first_name} ${profile.last_name}`.trim();
            }
          }

          const emailLogData = {
            message_id: result.MessageId,
            subject: processedSubject,
            aws_sender: sender || fromEmail,
            recipient_email: to,
            recipient_name: variables.fullName || `${variables.firstName} ${variables.lastName}`.trim(),
            content_html: processedHtml,
            content_text: processedText,
            template_id: template_id,
            status: 'sent',
            aws_response: result,
            configuration_set: configurationSet,
            variables: processedVariables,
            sent_at: new Date().toISOString(),
            recipient_id: recipient_id,
            initiator_id: user?.id || null,
            initiator_name: initiator_name
          };

          console.log('Attempting to log email with data:', {
            ...emailLogData,
            content_html: emailLogData.content_html?.substring(0, 100),
            content_text: emailLogData.content_text?.substring(0, 100),
            configuration_set: emailLogData.configuration_set || 'NOT SET'
          });

          const { error: insertError, data: insertData } = await supabase
            .from('email_logs')
            .insert(emailLogData)
            .select()
            .single();

          if (insertError) {
            console.error('Failed to log email:', insertError);
            console.error('Error details:', {
              message: insertError.message,
              details: insertError.details,
              hint: insertError.hint
            });
            throw insertError;
          }

          console.log('Successfully logged email:', {
            id: insertData.id,
            message_id: insertData.message_id,
            recipient_email: insertData.recipient_email,
            variables: insertData.variables
          });
        } catch (error) {
          console.error('Error logging email:', error);
          // Don't throw - we don't want logging failures to affect email sending
        }
      } else {
        console.warn('No Supabase client provided - skipping email logging');
      }

      return { 
        success: true, 
        messageId: result.MessageId,
        rawResponse: result,
        configuration_set: params.ConfigurationSetName 
      };
    } catch (error) {
      console.error('SES error:', error);
      throw new Error(`Failed to send email: ${error.message}`);
    }
  },

  // Send a test email
  async sendTestEmail(templateId, testVariables = {}, options = {}) {
    try {
      const { data: template, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (error) throw error;
      if (!template) throw new Error('Template not found');

      // Add test-specific variables
      const variables = {
        ...testVariables,
        sendDate: format(new Date(), 'MMMM dd, yyyy'),
        isTest: true
      };

      // Process content
      const html = this.replaceVariables(template.html_content, variables);
      const text = this.replaceVariables(template.text_content, variables);
      const subject = `[TEST] ${this.replaceVariables(template.subject, variables)}`;

      // Send the test email
      return await this.sendEmail(
        options.testEmail || import.meta.env.VITE_TEST_EMAIL || import.meta.env.TEST_EMAIL,
        subject,
        html,
        text,
        { ...options, isTest: true }
      );
    } catch (error) {
      console.error('Failed to send test email:', error);
      throw error;
    }
  },

  // Log an email
  async logEmail(logData) {
    try {
      console.log('Attempting to log email with data:', {
        message_id: logData.message_id,
        subject: logData.subject,
        recipient_email: logData.recipient_email,
        recipient_name: logData.recipient_name,
        template_id: logData.template_id,
        variables: logData.variables
      });
      
      // Ensure we have a valid message_id
      if (!logData.message_id) {
        console.error('Missing message_id in logData');
        return;
      }

      // Ensure recipient info is present
      if (!logData.recipient_email) {
        console.error('Missing recipient_email in logData');
        return;
      }

      const { error, data } = await supabase
        .from('email_logs')
        .insert({
          message_id: logData.message_id,
          subject: logData.subject,
          aws_sender: logData.aws_sender,
          recipient_email: logData.recipient_email,
          recipient_name: logData.recipient_name,
          content_html: logData.content_html,
          content_text: logData.content_text,
          template_id: logData.template_id,
          status: logData.status || 'sent',
          aws_response: logData.aws_response,
          configuration_set: logData.configuration_set,
          variables: logData.variables,
          sent_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Failed to log email - Supabase error:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        // Don't throw - we don't want logging failures to affect email sending
        return;
      }

      console.log('Successfully logged email:', {
        id: data.id,
        message_id: data.message_id,
        recipient_email: data.recipient_email,
        template_id: data.template_id
      });
    } catch (error) {
      console.error('Failed to log email:', error);
      console.error('Log data:', logData);
      // Don't throw - we don't want logging failures to affect email sending
    }
  }
};

// Log email service methods
console.log('🔍 unifiedEmailService methods:', Object.keys(unifiedEmailService));

export default unifiedEmailService;
